import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TableColumn from '@op/pages/Overview/CompareKeyMetrics/component/TableColumn';
import TableHeader from '@op/pages/Overview/CompareKeyMetrics/component/TableHeader';
import { CommonColumnsType } from '@op/types/table';
import { useIntl } from 'umi';
import { scrollToAnchorPoint, transLanguage } from '@op/utils';
import BubbleCommon from '@op/components/BubbleCommon';
import { formatDateFunc, formatTimeToShow } from '@op/utils/time';
import { NO_DATE } from '@op/constants';
import RegionCountryItem from '@op/components/Snippets/RegionCountryItem';
import ChannelLabel from '@op/components/ChannelLabel';
import CompareProgressColumn from '@op/pages/Compare/Compare/components/CompareProgressColumn';
import CardLoading from '@op/components/CardLoading';
import { reviewsFormat } from '@op/utils/value';
import _ from 'lodash-es';
import { metricsIconMap } from '@op/components/FilterCommon/widgets/MetricsSelect/config';
import { ParamsTimeType } from '@op/types/common';
import { dealWithOthersName } from '@op/utils/languageValue';
import moment from 'moment';

function useDeepCompareMemoize(value: any) {
  const ref = useRef();

  if (!_.isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

export default function useCompareCompetitorsColumns({
  columnsKey: propsColumnsKey,
  gameData: propsGameData,
  filter: propsFilter,
  showChange,
  activeSwitch,
  keyMetricsGroupingData: propsGroupingData,
  color: propsColor,
  getMetricsItem,
  dateType,
  showDate = true,
  MergeChannelIconElement,
}: {
  columnsKey: string[];
  gameData?: any;
  filter: any;
  showChange: boolean;
  activeSwitch: string;
  keyMetricsGroupingData: any;
  color: { [id: string]: string };
  getMetricsItem: (key: string) => any;
  dateType: ParamsTimeType;
  showDate?: boolean;
  MergeChannelIconElement?: any;
}) {
  const { formatMessage } = useIntl();
  const [columns, setColumns] = useState<Array<CommonColumnsType>>([]);
  const [sortColumns, setSortColumns] = useState<Array<string>>([]);

  const filter = useDeepCompareMemoize(propsFilter);
  const gameData = useDeepCompareMemoize(propsGameData ?? null);
  const memoizedColumnsKey = useDeepCompareMemoize(propsColumnsKey);
  const color = useDeepCompareMemoize(propsColor);

  const groupingData = useMemo(() => {
    if (activeSwitch) {
      return propsGroupingData;
    }
    return {};
  }, [activeSwitch, propsGroupingData]);

  const columnsKey = useMemo(
    () => memoizedColumnsKey ?? [],
    [memoizedColumnsKey],
  );

  const extraListView = ({
    extraListKey,
    item,
  }: {
    extraListKey: string;
    item: any;
  }) => {
    switch (extraListKey) {
      case 'language':
        return (
          <span className="extra-list-title">
            {transLanguage(item.name || '')}
          </span>
        );
      case 'channel':
        return <ChannelLabel channel_name={item.name}></ChannelLabel>;
      case 'region': {
        const name =
          item.name === 'global'
            ? 'Unidentified'
            : (item.name || '').toUpperCase();
        return dealWithOthersName(name, 'region');
      }
      default:
        return <RegionCountryItem isShortText market={item?.name || ''} />; // Handle unsupported keys
    }
  };

  const renderNoData = ({ data, key }: { data: any; key: string }) => {
    const { release_time: releaseTime }: any = gameData?.[data?.game_id] || {};
    const formatDate = (date: moment.MomentInput) => moment(date, 'YYYY-MM-DD');

    if (
      ['steam_recent_reviews', 'steam_recent_reviews_positive'].includes(key) &&
      !data?.[key]
    ) {
      if (
        !releaseTime ||
        formatDate(data.end_time).diff(formatDate(releaseTime), 'days') < 30
      ) {
        return (
          <div className="dbo-table-no-data">
            {formatMessage({
              id: 'Data available on the 31st day after launch',
            })}
          </div>
        );
      }
    }

    if (
      ['steam_all_reviews', 'steam_all_reviews_positive'].includes(key) &&
      !data?.[key]
    ) {
      if (
        !releaseTime ||
        formatDate(data.end_time).isBefore(formatDate(releaseTime), 'days')
      ) {
        return (
          <div className="dbo-table-no-data">
            {formatMessage({
              id: 'Data available after launch',
            })}
          </div>
        );
      }
    }

    return null;
  };

  const renderExtraList = ({
    data,
    metrics,
    key,
    formatData = reviewsFormat,
    noSplit,
  }: {
    data: {
      game_id: string;
      list: {
        [key: string]: Array<{ name: string; value: number }>;
      }[];
    };
    metrics: string;
    key: string;
    formatData: (value: number) => string | number;
    noSplit: string[];
  }) => {
    const { list } = data || {};
    const metricsData = list?.[metrics] || [];

    const noSupportSplit = noSplit?.includes(key);

    if (noSupportSplit) {
      return (
        <div className="dbo-table-none-extra-list">
          {formatMessage({ id: 'Metric splitting not supported' })}
        </div>
      );
    }
    if (metricsData.length === 0) {
      return (
        <div className="dbo-table-none-extra-list">
          {formatMessage({ id: 'No data under current filter selections' })}
        </div>
      );
    }
    return metricsData?.map((t) => (
      <div key={t.language || t.country} className="dbo-table-extra-list-flex">
        <div className="extra-list-box">
          {extraListView({ extraListKey: key, item: t })}
        </div>

        <div className="extra-list-text">
          <span className="text-num">{formatData(t.value)}</span>
        </div>
      </div>
    ));
  };

  const setOriginColumns = useCallback(() => {
    if (columnsKey?.length === 0) {
      return;
    }
    const sorting: string[] = [];
    const columns: Array<CommonColumnsType> = [
      {
        key: 'game',
        header: <TableHeader title={formatMessage({ id: 'Game' })} />,
        width: 325,
        fixed: 'left',
        render: (cvm: any) => {
          const item: any = gameData?.[cvm?.game_id] || {};
          return (
            <TableColumn
              isCurrentGame={
                item?.edition_unified_id === filter?.edition_unified_id
              }
              name={item?.entity_name}
              item={item}
              showPlatform={true}
              showReleaseDate={true}
              className="dbo-game-comparison-table"
              // extraContent={
              //   <span>
              //     {formatDateFunc(filter?.date_type ?? 'daily')(
              //       cvm?.start_time,
              //     )}
              //     ~{formatDateFunc(filter?.date_type ?? 'daily')(cvm?.end_time)}
              //   </span>
              // }
            />
          );
        },
      },
    ];

    if (showDate) {
      columns.push({
        key: 'date_range',
        header: <TableHeader title={formatMessage({ id: 'Date Range' })} />,
        width: 215,
        align: 'center',
        render: (cvm: any) => {
          if (
            ['Invalid date', '0000-00-00 00:00:00'].includes(cvm?.start_time) ||
            ['Invalid date', '0000-00-00 00:00:00'].includes(cvm?.end_time)
          ) {
            return <>-</>;
          }
          return (
            <>
              {`${formatTimeToShow({
                start_time: cvm?.start_time,
                end_time: cvm?.end_time,
                date_type: dateType,
              })}`}
            </>
          );
        },
      });
    }

    columnsKey?.forEach((val: string) => {
      const item = getMetricsItem(val);
      if (item) {
        const {
          tips,
          value: key,
          text: headerText,
          progress = true,
          format,
          align = 'right',
          icon,
          width,
          noSplit,
          ...res
        } = item || {};

        const iconImg = icon ? metricsIconMap[icon] : '';
        const text = formatMessage({ id: headerText || NO_DATE.EMPTY });
        columns.push({
          header: (
            <BubbleCommon
              placement="top"
              tipsMap={tips}
              showIcon={false}
              extraContent={
                MergeChannelIconElement && (
                  <MergeChannelIconElement
                    metric={key}
                  ></MergeChannelIconElement>
                )
              }
            >
              {iconImg && <img className="channel-icon" src={iconImg}></img>}
              {text}
            </BubbleCommon>
          ),
          text,
          key,
          align,
          width: width ?? 260,
          ...res,
          render: (cvm: any) => {
            const hasExtraList = activeSwitch !== '';
            const hideTrend = !!!showChange;
            const gameInfo: any = gameData?.[cvm?.game_id] || {};
            const { data, loading, error } = groupingData?.[`${cvm.id}`] || {};
            const currentGameGrouping =
              data?.grouping_list?.find((val) => val.game_id === cvm.game_id) ||
              {};

            const otherNoData = renderNoData({ data: cvm, key });

            if (otherNoData !== null) {
              return otherNoData;
            }

            return (
              <div key={cvm?.game_id + key}>
                <CompareProgressColumn
                  data={cvm}
                  metricInfo={item}
                  percentage={cvm[`${key}_ratio`]}
                  value={cvm[key]}
                  valueKey={key}
                  showProgress={progress}
                  hideTrend={hideTrend} 
                  formateValue={format}
                  formateDate={(value) =>
                    formatDateFunc(dateType ?? 'daily')(value)
                  }
                  color={color?.[cvm.id]}
                  gameInfo={gameInfo}
                  startTime={cvm.start_time}
                  endTime={cvm.end_time}
                  oldStartTime={cvm.link_of_start_time}
                  oldEndTime={cvm.link_of_end_time}
                  oldValue={cvm[`${key}_old`]}
                  title={text}
                  extraBubbleContent={
                    <span
                      className="bubble-action"
                      onClick={() => {
                        scrollToAnchorPoint(val);
                      }}
                    >
                      {formatMessage({ id: 'Go to the line graph' })}
                    </span>
                  }
                  showChange={showChange}
                />
                {hasExtraList && (
                  <CardLoading loading={loading} loadingText={''}>
                    {Object.values(currentGameGrouping).length > 0 &&
                      renderExtraList({
                        data: currentGameGrouping,
                        metrics: key,
                        key: activeSwitch,
                        formatData: format,
                        noSplit,
                      })}
                  </CardLoading>
                )}
              </div>
            );
          },
        });
        sorting.push(key);
      }
    });

    setColumns(columns);
    setSortColumns(sorting);
  }, [
    columnsKey,
    gameData,
    filter,
    activeSwitch,
    showChange,
    groupingData,
    formatMessage,
  ]);

  useEffect(() => {
    setOriginColumns();
  }, [setOriginColumns]);

  return { columns, sortColumns, setOriginColumns };
}
